import React from 'react';
import { Check, ExternalLink } from 'lucide-react';
import { Contact } from '../../types';
import { ContentFields } from './eventBuildUtils';

interface Props {
  content: ContentFields;
  onChange: (next: ContentFields) => void;
  contacts: Contact[];
  /** When true, LOCATION/DATE are read-only from Hostlinks. LINK and Introduction stay editable. */
  fromEvent: boolean;
}

const labelClass = 'block text-[10px] font-medium text-gray-500 dark:text-slate-400 mb-0.5';
const inputEditable =
  'block w-full rounded border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs py-1 px-2';
const inputReadonly =
  'block w-full rounded border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-900/50 text-xs text-gray-700 dark:text-slate-300 py-1 px-2';

const ContentPlaceholdersPanel: React.FC<Props> = ({
  content,
  onChange,
  contacts,
  fromEvent,
}) => {
  const set = (key: keyof ContentFields, value: string) => {
    onChange({ ...content, [key]: value });
  };

  const AutoFilled = () => (
    <span className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] font-medium text-emerald-700 dark:text-emerald-400">
      <Check className="h-2.5 w-2.5" strokeWidth={3} />
      Auto-filled
    </span>
  );

  const openLinkUrl = () => {
    const raw = content.LINK.trim();
    if (!raw) return;
    if (/^\s*(javascript|data):/i.test(raw)) return;
    const href = /^https?:\/\//i.test(raw) || raw.startsWith('//') ? raw : `https://${raw}`;
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const canOpenLink = Boolean(content.LINK.trim()) && !/^\s*(javascript|data):/i.test(content.LINK);

  return (
    <div className="bg-white dark:bg-slate-800 shadow-sm rounded-lg border border-gray-200 dark:border-slate-700 px-3 py-2.5">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400 mb-2">
        Content &amp; placeholders{' '}
        <span className="normal-case font-normal text-gray-400 dark:text-slate-500">(shared)</span>
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-2">
        <div>
          <label className={labelClass}>
            Location
            {fromEvent && <AutoFilled />}
          </label>
          <input
            type="text"
            readOnly={fromEvent}
            value={content.LOCATION}
            onChange={(e) => set('LOCATION', e.target.value)}
            className={fromEvent ? inputReadonly : inputEditable}
          />
        </div>
        <div>
          <label className={labelClass}>
            Date
            {fromEvent && <AutoFilled />}
          </label>
          <input
            type="text"
            readOnly={fromEvent}
            value={content.DATE}
            onChange={(e) => set('DATE', e.target.value)}
            className={fromEvent ? inputReadonly : inputEditable}
          />
        </div>
        <div>
          <label className={labelClass}>
            Link
            {fromEvent && content.LINK ? <AutoFilled /> : null}
          </label>
          <div className="flex items-stretch gap-1">
            <input
              type="text"
              value={content.LINK}
              onChange={(e) => set('LINK', e.target.value)}
              placeholder="Web URL from Hostlinks"
              className={`${inputEditable} min-w-0 flex-1`}
            />
            <button
              type="button"
              onClick={openLinkUrl}
              disabled={!canOpenLink}
              title={canOpenLink ? 'Open link in a new tab' : 'No link to open'}
              className="inline-flex items-center justify-center rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-600 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-600 hover:text-blue-600 dark:hover:text-blue-400 px-1.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white dark:disabled:hover:bg-slate-700 disabled:hover:text-gray-600"
            >
              <ExternalLink className="h-3.5 w-3.5" strokeWidth={2} />
              <span className="sr-only">Open link in a new tab</span>
            </button>
          </div>
        </div>

        <div>
          <label className={labelClass}>
            Introduction
            {fromEvent && content.INTRO ? <AutoFilled /> : null}
          </label>
          <textarea
            rows={2}
            value={content.INTRO}
            onChange={(e) => set('INTRO', e.target.value)}
            placeholder={fromEvent ? 'Host Name from Hostlinks' : 'Enter introduction…'}
            className={`${inputEditable} resize-y min-h-[2.25rem]`}
          />
        </div>
        <div>
          <label className={labelClass}>Discount</label>
          <input
            type="text"
            value={content.DISCOUNT}
            onChange={(e) => set('DISCOUNT', e.target.value)}
            placeholder="Discount code or offer…"
            className={inputEditable}
          />
        </div>
        <div>
          <label className={labelClass}>Extra Information</label>
          <textarea
            rows={2}
            value={content.EXTRA_INFO}
            onChange={(e) => set('EXTRA_INFO', e.target.value)}
            placeholder="Enter extra information…"
            className={`${inputEditable} resize-y min-h-[2.25rem]`}
          />
        </div>

        <div>
          <label className={labelClass}>
            Contact 1
            {fromEvent && content.CONTACT_1 ? <AutoFilled /> : null}
          </label>
          <select
            value={content.CONTACT_1}
            onChange={(e) => set('CONTACT_1', e.target.value)}
            className={inputEditable}
          >
            <option value="">Select…</option>
            {contacts.map((contact) => (
              <option key={contact.id} value={`${contact.name}\n${contact.details}`}>
                {contact.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Contact 2</label>
          <input
            type="text"
            value={content.CONTACT_2}
            onChange={(e) => set('CONTACT_2', e.target.value)}
            className={inputEditable}
          />
        </div>
        <div>
          <label className={labelClass}>Contact 3</label>
          <input
            type="text"
            value={content.CONTACT_3}
            onChange={(e) => set('CONTACT_3', e.target.value)}
            className={inputEditable}
          />
        </div>
      </div>
    </div>
  );
};

export default ContentPlaceholdersPanel;
